import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { debounceTime, Subject, takeUntil } from 'rxjs';

import * as fromSpaces from '../../../stores/spaces';
import { SpacesDispensesHD } from '../../interfaces/IHDspace';

@Component({
  selector: 'frodo-array-layout',
  templateUrl: './array-layout.component.html',
  styleUrls: ['./array-layout.component.scss'],
})
export class ArrayLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() selectSpace = new EventEmitter<SpacesDispensesHD>();
  @Input() spacesSelected: string[] = [];
  @Input() isRearSide = false;
  @ViewChild('layoutContainer') layoutContainer: ElementRef;
  @ViewChild('locker') locker: ElementRef;
  destroy$ = new Subject<void>();
  windowResize$ = new Subject<void>();
  spaces: SpacesDispensesHD[] = [];
  isLockerFullWidth = false;
  scale = 1;
  lockerHeightInches = 0;
  lockerWidthInches = 0;
  lockerHeightPx = 0;
  lockerWidthPx = 0;
  readonly EXTRA_SPACE_HEIGHT_INCHES = 10;
  readonly SPACES_WIDTH_INCHES: number = 16;
  readonly SPACE_WIDTH_PX: number = 170;

  constructor(private store: Store, private cdRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.store.dispatch(fromSpaces.loadSpaces());
    this.getSpaces();
    this.resizeLocker();
  }

  ngAfterViewInit(): void {
    this.computeScale();
    if (this.isRearSide) {
      this.scrollLockerToEnd();
    }
    this.cdRef.detectChanges();
  }

  @HostListener('window:resize')
  onResize() {
    this.windowResize$.next();
  }

  computeLockerRealMeasure(): void {
    if (!this.spaces.length) return;

    let maxX = 0;
    let maxY = 0;

    this.spaces.forEach(({ hardwareInfo }: SpacesDispensesHD) => {
      maxX = Math.max(maxX, hardwareInfo.coordinates.x + hardwareInfo.width);
      maxY = Math.max(maxY, hardwareInfo.coordinates.y + hardwareInfo.height);
    });

    this.lockerHeightInches = maxY;
    this.lockerWidthInches = maxX;
  }

  computeScale(): void {
    if (!this.lockerHeightInches || !this.locker) return;

    this.lockerHeightPx = this.locker.nativeElement.clientHeight;
    this.scale = this.lockerHeightPx / this.lockerHeightInches;
    this.lockerWidthPx = (this.lockerWidthInches / this.SPACES_WIDTH_INCHES) * this.SPACE_WIDTH_PX;
    const layoutContainerWidth = this.layoutContainer.nativeElement.clientWidth;
    if (this.lockerWidthPx <= layoutContainerWidth) {
      this.isLockerFullWidth = true;
    }
  }

  resizeLocker(): void {
    this.windowResize$.pipe(debounceTime(100), takeUntil(this.destroy$)).subscribe(() => {
      this.computeScale();
    });
  }

  getSpaces(): void {
    this.store
      .select(fromSpaces.selectSpaces)
      .pipe(takeUntil(this.destroy$))
      .subscribe((spaces: SpacesDispensesHD[]) => {
        this.spaces = spaces;
        this.computeLockerRealMeasure();
      });
  }

  scrollLocker(delta: number) {
    if (this.locker && this.locker.nativeElement) {
      this.locker.nativeElement.scrollTo({
        left: this.locker.nativeElement.scrollLeft + delta,
        behavior: 'smooth',
      });
    }
  }

  scrollLockerToEnd() {
    if (this.locker && this.locker.nativeElement) {
      this.locker.nativeElement.scrollTo({
        left: this.locker.nativeElement.clientWidth,
        behavior: 'smooth',
      });
    }
  }

  prev() {
    this.scrollLocker(-this.SPACE_WIDTH_PX);
  }

  next() {
    this.scrollLocker(this.SPACE_WIDTH_PX);
  }

  handleSpaceClick(space: SpacesDispensesHD) {
    this.selectSpace.emit(space);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
